import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
// import { getSessionItem, setSessionItem } from '../../../shared/utils/config/sessionStorage';
import { getCookie, setCookie } from '../../../shared/utils/config/cookies';
import { VERIFY_AGE, VERIFY_AGE_SET } from '../../../shared/constants/common';
import useNavigation from '../../../shared/utils/useNavigation';
import { CONFIG } from '../../../shared/constants/common';
import './warning-modal.css';
import { ROUTES } from '../../../shared/constants/routes.js';

const WarningModal = () => {
  const navigate = useNavigation();
  const location = useLocation();
  // const verifyAge = getSessionItem(VERIFY_AGE);
  const verifyAge = getCookie(VERIFY_AGE) === 'true';
  const [isModalVisible, setIsModalVisible] = useState(!verifyAge);
  const [isAgreeing, setIsAgreeing] = useState(false);
  const [showScrollIcon, setShowScrollIcon] = useState(true);
  const modalRef = useRef(null);


  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const handleScroll = () => {
      if (modal.scrollTop > 0) {
        setShowScrollIcon(false);
      } else {
        setShowScrollIcon(true);
      }
    };

    modal.addEventListener("scroll", handleScroll);

    return () => {
      modal.removeEventListener("scroll", handleScroll);
    };
  }, []);


  const handleScrollDown = () => {
    const modal = modalRef.current;
    if (!modal) return;

    modal.scrollTo({
      top: modal.scrollHeight,
      behavior: "smooth", // Smooth scrolling animation
    });
  };



  const handleAgree = useCallback(() => {
    // setSessionItem(VERIFY_AGE, 'true');
    // window.dispatchEvent(new Event('verifyAgeSet'));
    setCookie(VERIFY_AGE, 'true');
    window.dispatchEvent(new Event(VERIFY_AGE_SET));
  }, []);

  const handleAgreeAndClose = useCallback(() => {
    setIsAgreeing(true);
    setIsModalVisible(false);
    handleAgree();
    document.body.classList.remove('show_warning_modal')
  }, [handleAgree]);

  const handleDisagree = useCallback(() => {
    navigate.toExternal('https://www.google.com');
  }, [navigate]);

  const handleTermsClick = useCallback(() => {
    navigate.toTerms();
  }, [navigate]);

  const handlePrivacyClick = useCallback(() => {
    navigate.toPrivacy();
  }, [navigate]);

  useEffect(() => {
    // const isVerifiedAge = getSessionItem(VERIFY_AGE);
    const isVerifiedAge = getCookie(VERIFY_AGE) === 'true';
    if (isVerifiedAge) return;

    document.body.classList.add('show_warning_modal');
    return () => {
      document.body.classList.remove('show_warning_modal');
    }
  }, [])

  // Don't show modal on terms-of-service or privacy-policy pages
  const shouldHideModal = useMemo(() => {
    const currentPath = location.pathname;
    return currentPath === ROUTES.LEGAL_TERMS || currentPath === ROUTES.LEGAL_PRIVACY;
  }, [location.pathname]);

  // Keep body class in sync so we don't accidentally hide the main app
  // (e.g. `.show_warning_modal .discover-screen { display: none }`).
  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (shouldHideModal || !isModalVisible) {
      document.body.classList.remove('show_warning_modal');
    } else {
      document.body.classList.add('show_warning_modal');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('show_warning_modal');
    };
  }, [shouldHideModal, isModalVisible]);

  if (shouldHideModal || !isModalVisible) return null;

  return (
    <div
      className="modern-warning-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="warning-modal-title"
      aria-describedby="warning-modal-description"
    >
      {/* Background Effects */}
      <div className="modern-warning-bg-overlay" aria-hidden="true">
        <div className="modern-warning-bg-blur-1"></div>
        <div className="modern-warning-bg-blur-2"></div>
        <div className="modern-warning-bg-blur-3"></div>
        <div className="modern-warning-bg-blur-4"></div>
      </div>

      {/* Main Modal */}
      <div className="modern-warning-modal">

        <div className="modern-warning-scroll" ref={modalRef}>
          <div className="modern-warning-content">

            {/* Warning Icon */}
            <div className="modern-warning-icon-section">
              <div className="modern-warning-icon-container">
                <div className="modern-warning-icon-bg">
                  <i className="fas fa-exclamation-triangle" />
                </div>
              </div>
            </div>

            {/* Header */}
            {/* <header className="modern-warning-header">
              <h1 id="warning-modal-title" className="modern-warning-header-text">
                Age Verification Required
              </h1>
            </header> */}

            {/* Content Sections */}
            <section className="modern-warning-content-sections" id="warning-modal-description">
              <article className="modern-warning-section">
                <header className="modern-warning-section-header">
                  <div className="modern-warning-section-icon" aria-hidden="true">
                    <i className="fas fa-exclamation modern-warning-small-icon" />
                  </div>
                  <div>
                    <h2 className="modern-warning-section-title">18+ Only</h2>
                  </div>
                </header>
                <p className="modern-warning-section-text">
                  This website is for use solely by responsible adults over 18-years old (or the age of consent in the jurisdiction from which it is being accessed). The materials that are available within this website include graphic visual depictions and descriptions of nudity and sexual activity and must not be accessed by anyone who is under 18-years old and the age of consent. Visiting this website if you are under 18-years old and the age of consent may be prohibited by the law of your jurisdiction.
                </p>
              </article>

              <article className="modern-warning-section">
                <header className="modern-warning-section-header">
                  <div className="modern-warning-section-icon" aria-hidden="true">
                    <i className="fas fa-handshake" />
                  </div>
                  <div>
                    <h2 className="modern-warning-section-title">Terms Agreement</h2>
                  </div>
                </header>
                <p className="modern-warning-section-text">
                  By clicking "I Agree" below, you state that the following statements are accurate and agree to our{' '}
                  <span
                    onClick={handleTermsClick}
                    className="modern-warning-link"
                    style={{ cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTermsClick();
                      }
                    }}
                  >
                    terms of service
                  </span>
                  {' '}&{' '}
                  <span
                    onClick={handlePrivacyClick}
                    className="modern-warning-link"
                    style={{ cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handlePrivacyClick();
                      }
                    }}
                  >
                    privacy policy
                  </span>
                  :
                </p>
              </article>
            </section>

            {/* Agreement Items */}
            <section className="modern-warning-agreement-items">
              <div className="modern-warning-agreement-container">
                <h2 className="sr-only">Terms and Conditions Agreement</h2>
                <ul className="modern-warning-agreement-list" role="list">
                  <li className="modern-warning-agreement-item">
                    <div className="modern-warning-check-icon" aria-hidden="true">
                      <i className="fas fa-check" />
                    </div>
                    <span>
                      I am an adult, at least 18-years old, and the age of consent in my jurisdiction, and I have the legal right to access and possess adult material in my community.
                    </span>
                  </li>
                  <li className="modern-warning-agreement-item">
                    <div className="modern-warning-check-icon" aria-hidden="true">
                      <i className="fas fa-check" />
                    </div>
                    <span>
                      I will leave this website promptly if I am in any way offended by the sexual nature of any material.
                    </span>
                  </li>
                  <li className="modern-warning-agreement-item">
                    <div className="modern-warning-check-icon" aria-hidden="true">
                      <i className="fas fa-check" />
                    </div>
                    <span>
                      I will not permit any person under 18-years old to have access to any of the materials contained within this website.
                    </span>
                  </li>
                  <li className="modern-warning-agreement-item">
                    <div className="modern-warning-check-icon" aria-hidden="true">
                      <i className="fas fa-check" />
                    </div>
                    <span>
                      I understand and will abide by the standards and laws of my community.
                    </span>
                  </li>
                  <li className="modern-warning-agreement-item">
                    <div className="modern-warning-check-icon" aria-hidden="true">
                      <i className="fas fa-check" />
                    </div>
                    <span>
                      I am voluntarily choosing to access this website because I want to view, read, or hear the various available materials.
                    </span>
                  </li>
                  <li className="modern-warning-agreement-item">
                    <div className="modern-warning-check-icon" aria-hidden="true">
                      <i className="fas fa-check" />
                    </div>
                    <span>
                      By logging on and viewing any part of the website, I will not hold the website's owners or its employees responsible for any materials located on the website.
                    </span>
                  </li>
                  <li className="modern-warning-agreement-item">
                    <div className="modern-warning-check-icon" aria-hidden="true">
                      <i className="fas fa-check" />
                    </div>
                    <span>
                      I do not find images of nude adults, adults engaged in sexual acts, or other sexual material to be offensive or objectionable.
                    </span>
                  </li>
                  <li className="modern-warning-agreement-item">
                    <div className="modern-warning-check-icon" aria-hidden="true">
                      <i className="fas fa-check" />
                    </div>
                    <span>
                      I acknowledge that the website's Terms-of-Service Agreement governs my use of the website, and I have reviewed and agreed to be bound by those terms.
                    </span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Age Verification Section */}
            <section className="modern-warning-age-verification">
              <div className="modern-warning-verification-container">
                <div className="modern-warning-verification-content">
                  <div className="modern-warning-verification-icon" aria-hidden="true">
                    <i className="fas fa-id-card" />
                  </div>
                  <div className="modern-warning-verification-text">
                    {/* <h3 className="modern-warning-verification-title">Age Verification Required</h3> */}
                    <p className="modern-warning-verification-description">
                      You must be of legal age in your jurisdiction to access this content. By proceeding, you confirm that you meet all legal requirements.
                    </p>
                    <div className="modern-warning-verification-badges">
                      <div className="modern-warning-badge">
                        <i className="fas fa-calendar-alt modern-warning-small-icon" aria-hidden="true" />
                        <span>18+ Required</span>
                      </div>
                      <div className="modern-warning-badge">
                        <i className="fas fa-globe-americas modern-warning-small-icon" aria-hidden="true" />
                        <span>Local Laws Apply</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
        {showScrollIcon && <span onClick={handleScrollDown} className="cm_scroll_icon cm_scroll_icon2">Scroll down <b>↓</b></span>}

        {/* Action Buttons */}
        <section className="modern-warning-actions">
          <button
            type="button"
            className="modern-warning-agree-btn"
            onClick={handleAgreeAndClose}
            disabled={isAgreeing}
            aria-label="I agree to the terms and conditions"
          >
            {isAgreeing ? (
              <span className="modern-warning-btn-loading">
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
              </span>
            ) : (
              <span className="modern-warning-btn-content">
                <i id="modern-circle" className="fas fa-check-circle" aria-hidden="true" />
                <span>I Agree - Enter Site</span>
              </span>
            )}
            <div className="modern-warning-btn-shimmer" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="modern-warning-disagree-btn"
            onClick={handleDisagree}
            aria-label="I disagree and want to leave the site"
          >
            <span className="modern-warning-btn-content">
              <i className="fas fa-times-circle" aria-hidden="true" />
              <span>I Disagree - Leave Site</span>
            </span>
          </button>

          <nav className="modern-warning-secondary-actions" aria-label="Legal documents">
            <button
              type="button"
              onClick={handleTermsClick}
              className="modern-warning-secondary-btn"
            >
              <i className="fas fa-file-contract modern-warning-white-icon" aria-hidden="true" />
              <span>Terms of Service</span>
            </button>
            <button
              type="button"
              onClick={handlePrivacyClick}
              className="modern-warning-secondary-btn"
            >
              <i className="fas fa-shield-alt modern-warning-white-icon" aria-hidden="true" />
              <span>Privacy Policy</span>
            </button>
          </nav>


        </section>

        {/* Footer Notice */}
        <footer className="modern-warning-footer">
          <div className="modern-warning-footer-notice">
            <span>
              For billing inquiries or to cancel your membership please visit{' '}
              <a
                href={CONFIG.CCBILL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="modern-warning-ccbill-link"
              >
                ccbill.com
              </a>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default WarningModal;
